import React, { useEffect, useState } from "react";
import api, { formatMoney, formatDate } from "@/lib/api";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Legend,
} from "recharts";
import { TrendingUp, TrendingDown, AlertTriangle, CalendarClock, Wallet, ArrowUpRight, ArrowDownRight, Banknote } from "lucide-react";
import { Badge } from "@/components/ui/badge";

const chartTooltipStyle = {
  contentStyle: {
    background: "#0B111C",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: 8,
    fontSize: 12,
  },
  labelStyle: { color: "#F59E0B" },
};

function KpiCard({ label, value, hint, icon: Icon, tone = "amber", testid }) {
  const tones = {
    amber: "border-amber-500/25 text-amber-300",
    green: "border-emerald-500/25 text-emerald-300",
    red: "border-red-500/25 text-red-300",
    blue: "border-blue-500/25 text-blue-300",
    slate: "border-white/10 text-slate-200",
  };
  return (
    <div className={`card-elevated rounded-2xl p-5 relative overflow-hidden ${tones[tone]}`} data-testid={testid}>
      <div className="flex items-start justify-between">
        <div className="text-[11px] mono uppercase tracking-[0.2em] text-slate-400">{label}</div>
        <Icon className={`w-4 h-4 ${tone === "amber" ? "text-amber-400" : tone === "green" ? "text-emerald-400" : tone === "red" ? "text-red-400" : tone === "blue" ? "text-blue-400" : "text-slate-400"}`} />
      </div>
      <div className="mt-3 text-3xl font-bold tracking-tight tabular">{value}</div>
      {hint && <div className="mt-1 text-xs text-slate-400">{hint}</div>}
    </div>
  );
}

export default function Dashboard() {
  const [summary, setSummary] = useState(null);
  const [monthly, setMonthly] = useState([]);
  const [upcoming, setUpcoming] = useState([]);

  useEffect(() => {
    (async () => {
      const [s, m, u] = await Promise.all([
        api.get("/summary/dashboard"),
        api.get("/summary/monthly"),
        api.get("/summary/upcoming", { params: { days: 30 } }),
      ]);
      setSummary(s.data);
      setMonthly(m.data);
      setUpcoming(u.data);
    })();
  }, []);

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="mono text-xs uppercase tracking-[0.3em] text-amber-400">Kontrol Paneli</div>
          <h1 className="text-4xl font-bold tracking-tight mt-1">Finansal Genel Bakış</h1>
          <p className="text-slate-400 mt-1 text-sm">Aves Elektrik Otomasyon · Anlık nakit akışı ve cari durumu</p>
        </div>
        <div className="text-right">
          <div className="mono text-[11px] uppercase tracking-widest text-slate-400">Bugün</div>
          <div className="text-lg font-semibold">
            {new Date().toLocaleDateString("tr-TR", { day: "2-digit", month: "long", year: "numeric" })}
          </div>
        </div>
      </header>

      {/* KPI Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 stagger">
        <KpiCard label="Toplam Alacak" value={formatMoney(summary?.total_receivable)} icon={ArrowDownRight} tone="amber" hint="Tahsil edilecek" testid="kpi-receivable" />
        <KpiCard label="Toplam Borç" value={formatMoney(summary?.total_payable)} icon={ArrowUpRight} tone="red" hint="Ödenecek" testid="kpi-payable" />
        <KpiCard label="Vadesi Geçen (Alacak)" value={formatMoney(summary?.overdue_receivable)} icon={AlertTriangle} tone="red" hint="Vade farkı işleyebilir" testid="kpi-overdue" />
        <KpiCard label="Net Nakit Akışı" value={formatMoney(summary?.net_cashflow)} icon={Wallet} tone="green" hint={`Kâr: ${formatMoney(summary?.profit)}`} testid="kpi-cashflow" />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
        <div className="xl:col-span-2 card-elevated rounded-2xl p-6" data-testid="cashflow-chart-card">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold">Aylık Nakit Akışı</h3>
              <p className="text-xs text-slate-400 mono uppercase tracking-widest">Son 12 Ay · Gelir vs Gider</p>
            </div>
            <Badge className="bg-amber-500/15 text-amber-300 border-amber-500/25 hover:bg-amber-500/20">
              <TrendingUp className="w-3 h-3 mr-1" /> Trend
            </Badge>
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={monthly}>
              <defs>
                <linearGradient id="incG" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10B981" stopOpacity={0.4} />
                  <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="expG" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#EF4444" stopOpacity={0.4} />
                  <stop offset="95%" stopColor="#EF4444" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#94a3b8" }} />
              <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}K`} />
              <Tooltip {...chartTooltipStyle} formatter={(v) => formatMoney(v)} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Area type="monotone" dataKey="income" name="Gelir" stroke="#10B981" fill="url(#incG)" strokeWidth={2} />
              <Area type="monotone" dataKey="expense" name="Gider" stroke="#EF4444" fill="url(#expG)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="card-elevated rounded-2xl p-6" data-testid="profit-chart-card">
          <h3 className="text-lg font-semibold mb-1">Aylık Kâr</h3>
          <p className="text-xs text-slate-400 mono uppercase tracking-widest mb-4">Kâr / Zarar (₺)</p>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={monthly}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="month" tick={{ fontSize: 10, fill: "#94a3b8" }} />
              <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}K`} />
              <Tooltip {...chartTooltipStyle} formatter={(v) => formatMoney(v)} />
              <Bar dataKey="profit" name="Kâr" fill="#F59E0B" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Upcoming payments */}
      <div className="card-elevated rounded-2xl p-6" data-testid="upcoming-payments-card">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <CalendarClock className="w-4 h-4 text-amber-400" /> Yaklaşan & Vadesi Geçen Ödemeler
            </h3>
            <p className="text-xs text-slate-400 mt-1">Sonraki 30 gün · Otomatik vade farkı hesaplaması dahil</p>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase mono tracking-widest text-slate-500 border-b border-white/5">
                <th className="py-3 pr-4">Firma</th>
                <th className="py-3 pr-4">Açıklama</th>
                <th className="py-3 pr-4">Tip</th>
                <th className="py-3 pr-4">Vade</th>
                <th className="py-3 pr-4">Tutar</th>
                <th className="py-3 pr-4">Vade Farkı</th>
                <th className="py-3">Durum</th>
              </tr>
            </thead>
            <tbody>
              {upcoming.length === 0 && (
                <tr><td colSpan={7} className="py-8 text-center text-slate-500">Yaklaşan ödeme yok</td></tr>
              )}
              {upcoming.map((t) => (
                <tr key={t.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`upcoming-row-${t.id}`}>
                  <td className="py-3 pr-4 font-medium">{t.company_name}</td>
                  <td className="py-3 pr-4 text-slate-300">{t.description}</td>
                  <td className="py-3 pr-4">
                    {t.type === "sale" ? (
                      <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/25">Alacak</Badge>
                    ) : (
                      <Badge className="bg-red-500/15 text-red-300 border-red-500/25">Borç</Badge>
                    )}
                  </td>
                  <td className="py-3 pr-4 tabular text-slate-300">{formatDate(t.due_date)}</td>
                  <td className="py-3 pr-4 tabular font-semibold">{formatMoney(t.amount)}</td>
                  <td className="py-3 pr-4 tabular text-amber-300">
                    {t.overdue_days > 0 ? `+${formatMoney(t.auto_vade_farki)} (${t.overdue_days}g)` : "-"}
                  </td>
                  <td className="py-3">
                    {t.days_diff < 0 ? (
                      <Badge className="bg-red-500/15 text-red-300 border-red-500/25">Vadesi geçti</Badge>
                    ) : t.days_diff === 0 ? (
                      <Badge className="bg-amber-500/15 text-amber-300 border-amber-500/25">Bugün</Badge>
                    ) : (
                      <Badge className="bg-blue-500/15 text-blue-300 border-blue-500/25">{t.days_diff} gün</Badge>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
